import { Box, DestructiveModal, Text } from '@modusign/design-system';
import { useSelector } from 'react-redux';

import useMemberPermissions from '@app/hooks/useMemberPermission';
import josa from '@app/libs/josa';
import { getName } from '@app/store/user/user/selectors';

interface DeleteExternalDocumentModalProps {
  documents: { title: string }[];
  isOpen: boolean;
  isLoading: boolean;
  context: 'BATCH_ACTION' | 'CONTEXT_MENU';
  onConfirm: () => void;
  onCancel: () => void;
}

export default function DeleteExternalDocumentModal({
  documents,
  isLoading,
  isOpen,
  context,
  onCancel,
  onConfirm,
}: DeleteExternalDocumentModalProps) {
  const { permissionMap } = useMemberPermissions();
  const hasDeletePermission = permissionMap.DELETE_EXTERNAL_DOCUMENT;
  const userName = useSelector(getName);

  if (documents.length === 0) {
    return null;
  }

  // 뷰 분기 피그마 링크: https://www.figma.com/file/MQG1zAF12Y441HRG3dPwwt/%EC%9B%8C%ED%81%AC%EC%8A%A4%ED%8E%98%EC%9D%B4%EC%8A%A4(%EC%A1%B0%EC%A7%813.0)-hi-fi?type=design&node-id=5421-164927&t=VJKtXz0eTuboS4di-4
  let description: string;
  if (context === 'CONTEXT_MENU') {
    description = `${josa(`'${documents[0].title}'`, '을/를')} 완전히 삭제할까요?`;
  } else if (!hasDeletePermission) {
    description = `선택한 ${documents.length}개의 계약 중 ${userName}님이 편집자인 계약을 완전히 삭제할까요?`;
  } else {
    description = `'${documents[0].title}'${
      documents.length === 1
        ? josa.only(documents[0].title, '을/를')
        : `외 ${documents.length - 1}개의 계약을`
    } 완전히 삭제할까요?`;
  }

  return (
    <DestructiveModal
      closeButtonLabel="취소"
      confirmButtonLabel="삭제"
      isLoading={isLoading}
      isOpen={isOpen}
      maxWidth="xs"
      title="계약 삭제"
      variant="destructive"
      onClose={onCancel}
      onConfirm={onConfirm}
    >
      <Text textAlign="center">{description}</Text>
      <Text color="textNegativeBase" sx={{ mt: 3 }} textAlign="center">
        삭제한 계약은 복구할 수 없습니다.
      </Text>
      <Box
        bg="boxBackgroundNegativeBase"
        border="1px solid"
        borderColor="boxBorderNegativeBase"
        lineHeight={1.5}
        mt={6}
        px={6}
        py={7}
      >
        계약서를 포함한 모든 파일과 데이터가 삭제됩니다.
      </Box>
    </DestructiveModal>
  );
}
